import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';

export type ReleaseNoteAudience = 'hospital' | 'specialist';
export type ReleaseNoteStatus = 'draft' | 'scheduled' | 'published';
export type ReleaseNotePublishMode = 'draft' | 'now' | 'scheduled';

export interface ReleaseNote {
  _id?: string;
  title: string;
  content: string;
  targetAudiences: ReleaseNoteAudience[];
  status?: ReleaseNoteStatus;
  effectiveStatus?: ReleaseNoteStatus;
  publishMode?: ReleaseNotePublishMode;
  scheduledPublishAt?: string | Date | null;
  publishedAt?: string | Date | null;
  isRead?: boolean;
  canEditContent?: boolean;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export interface ReleaseNotesPage {
  releaseNotes: ReleaseNote[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

@Injectable({
  providedIn: 'root'
})
export class ReleaseNotesService {
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(private api: ApiService) { }

  getAdminReleaseNotes(params: any = {}): Observable<any> {
    return this.api.apiRequest('get', 'admin/release-notes', params);
  }

  getAdminReleaseNote(id: string): Observable<any> {
    return this.api.apiRequest('get', `admin/release-notes/${id}`, {});
  }

  createReleaseNote(note: ReleaseNote): Observable<any> {
    return this.api.apiRequest('post', 'admin/release-notes', note);
  }

  updateReleaseNote(id: string, note: Partial<ReleaseNote>): Observable<any> {
    return this.api.apiRequest('put', `admin/release-notes/${id}`, note);
  }

  publishReleaseNote(id: string): Observable<any> {
    return this.api.apiRequest('put', `admin/release-notes/${id}/publish`, {});
  }

  deleteReleaseNote(id: string): Observable<any> {
    return this.api.apiRequest('delete', `admin/release-notes/${id}`, {});
  }

  getUserReleaseNotes(params: any = {}): Observable<ReleaseNote[]> {
    return this.getUserReleaseNotesPage(params).pipe(map(page => page.releaseNotes));
  }

  getUserReleaseNotesPage(params: any = {}): Observable<ReleaseNotesPage> {
    return this.api.apiRequest('get', 'user/release-notes', params).pipe(
      map(res => ({
        releaseNotes: res?.data?.data || [],
        pagination: res?.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 }
      })),
      catchError(err => {
        console.error('[ReleaseNotes] Error loading release notes:', err);
        return of({ releaseNotes: [], pagination: { page: 1, limit: 20, total: 0, pages: 0 } });
      })
    );
  }

  getUserReleaseNote(id: string): Observable<ReleaseNote | null> {
    return this.api.apiRequest('get', `user/release-notes/${id}`, {}).pipe(
      map(res => res?.data?.data || null)
    );
  }

  getUnreadReleaseNotes(): Observable<{ count: number; releaseNotes: ReleaseNote[] }> {
    return this.api.apiRequest('get', 'user/release-notes/unread', {}).pipe(
      map(res => {
        const data = res?.data?.data || { count: 0, releaseNotes: [] };
        this.unreadCountSubject.next(data.count || 0);
        return data;
      }),
      catchError(err => {
        console.error('[ReleaseNotes] Error loading unread release notes:', err);
        this.unreadCountSubject.next(0);
        return of({ count: 0, releaseNotes: [] });
      })
    );
  }

  refreshUnreadCount(): void {
    this.getUnreadReleaseNotes().subscribe();
  }

  markReleaseNoteRead(id: string): Observable<any> {
    return this.api.apiRequest('post', `user/release-notes/${id}/read`, {}).pipe(
      map(res => {
        this.refreshUnreadCount();
        return res;
      })
    );
  }

  markAllReleaseNotesRead(): Observable<any> {
    return this.api.apiRequest('post', 'user/release-notes/mark-all-read', {}).pipe(
      map(res => {
        this.unreadCountSubject.next(0);
        return res;
      })
    );
  }
}
